import React, { useRef, useEffect } from "react";
import settlementTypes from "../assets/allThePoints/settlementTypes.json";

// Import all legend icons statically
import green_circle from "../assets/allThePoints/legend/green_circle.png";
import green_square from "../assets/allThePoints/legend/green_square.png";
import green_star_A3 from "../assets/allThePoints/legend/green_star_A3.png";
import green_diamond_A4 from "../assets/allThePoints/legend/green_diamond_A4.png";
import green_triangle from "../assets/allThePoints/legend/green_triangle.png";
import green_donut from "../assets/allThePoints/legend/green_donut.png";
import green_question from "../assets/allThePoints/legend/green_question.png";
import maroon_circle from "../assets/allThePoints/legend/maroon_circle.png";
import purple_circle_C from "../assets/allThePoints/legend/purple_circle_C.png";
import pink_circle from "../assets/allThePoints/legend/pink_circle.png";
import pink_square from "../assets/allThePoints/legend/pink_square.png";
import pink_star from "../assets/allThePoints/legend/pink_star.png";
import red_circle from "../assets/allThePoints/legend/red_circle.png";
import red_square from "../assets/allThePoints/legend/red_square.png";
import red_triangle_K4 from "../assets/allThePoints/legend/red_triangle_K4.png";
import red_diamond from "../assets/allThePoints/legend/red_diamond.png";
import red_star_of_david from "../assets/allThePoints/legend/red_star_of_david.png";
import Red_star_K7 from "../assets/allThePoints/legend/Red_star_K7.png";
import red_question_mark from "../assets/allThePoints/legend/red_question_mark.png";
import black_circle from "../assets/allThePoints/legend/black_circle.png";
import black_square_U3 from "../assets/allThePoints/legend/black_square_U3.png";
import black_triangle from "../assets/allThePoints/legend/black_triangle.png";
import black_question from "../assets/allThePoints/legend/black_question.png";
import red_black_circle from "../assets/allThePoints/legend/red_black_circle.png";
import Orange_circle_N from "../assets/allThePoints/legend/Orange_circle_N.png";
import yellow_circle_O from "../assets/allThePoints/legend/yellow_circle_O.png";
import blue_circle from "../assets/allThePoints/legend/blue_circle.png";
import blue_green_circle from "../assets/allThePoints/legend/blue_green_circle.png";
import dark_blue_circle from "../assets/allThePoints/legend/dark_blue_circle.png";
import grey_circle from "../assets/allThePoints/legend/grey_circle.png";
import grey_square from "../assets/allThePoints/legend/grey_square.png";
import grey_star_of_david from "../assets/allThePoints/legend/grey_star_of_david.png";
import grey_triangle from "../assets/allThePoints/legend/grey_triangle.png";
import grey_diamond from "../assets/allThePoints/legend/grey_diamond.png";
import grey_question from "../assets/allThePoints/legend/grey_question.png";

// Icon mapping (keys now include .png)
const iconMapping = {
  "green_circle.png": green_circle,
  "green_square.png": green_square,
  "green_star_A3.png": green_star_A3,
  "green_diamond_A4.png": green_diamond_A4,
  "green_triangle.png": green_triangle,
  "green_donut.png": green_donut,
  "green_question.png": green_question,
  "maroon_circle.png": maroon_circle,
  "purple_circle_C.png": purple_circle_C,
  "pink_circle.png": pink_circle,
  "pink_square.png": pink_square,
  "pink_star.png": pink_star,
  "red_circle.png": red_circle,
  "red_square.png": red_square,
  "red_triangle_K4.png": red_triangle_K4,
  "red_diamond.png": red_diamond,
  "red_star_of_david.png": red_star_of_david,
  "Red_star_K7.png": Red_star_K7,
  "red_question_mark.png": red_question_mark,
  "black_circle.png": black_circle,
  "black_square_U3.png": black_square_U3,
  "black_triangle.png": black_triangle,
  "black_question.png": black_question,
  "red_black_circle.png": red_black_circle,
  "Orange_circle_N.png": Orange_circle_N,
  "yellow_circle_O.png": yellow_circle_O,
  "blue_circle.png": blue_circle,
  "blue_green_circle.png": blue_green_circle,
  "dark_blue_circle.png": dark_blue_circle,
  "grey_circle.png": grey_circle,
  "grey_square.png": grey_square,
  "grey_star_of_david.png": grey_star_of_david,
  "grey_triangle.png": grey_triangle,
  "grey_diamond.png": grey_diamond,
  "grey_question.png": grey_question
};

// Color mapping based on settlementTypes.json
const colorMapping = {
  green: "#48752C",
  maroon: "#7A2917", 
  purple: "#8C1AF5",
  pink: "#EA33F7",
  red: "#B83F29",
  black: "#000000",
  orange: "#F19E38",
  yellow: "#FFFF54",
  blue: "#5885E1",
  blue_green: "#4ECDC4",
  dark_blue: "#45B7D1",
  grey: "#CCCCCC",
  red_black: "#8B0000"
};

const LegendGroup = ({
  group,
  iconImports,
  isChecked,
  setIsChecked,
  isOpen,
  toggleOpen,
  advancedMode,
  allThePoints = [],
  groupData = null,
}) => {
  const collapsibleRef = useRef(null);

  // Function to derive community groups from API data using settlementTypes.json
  const deriveCommunityGroups = (allThePoints) => {
    if (!allThePoints || !Array.isArray(allThePoints)) {
      return {};
    }

    const groups = {};
    
    allThePoints.forEach(point => {
      const settlementClass = point.SettlementClassification || "";
      if (settlementClass.trim()) {
        const settlementType = settlementTypes[settlementClass];
        
        if (settlementType) {
          const groupName = settlementType.group;
          
          if (!groups[groupName]) {
            groups[groupName] = {
              name: groupName,
              color: colorMapping[settlementType.color] || "#CCCCCC",
              shape: settlementType.shape,
              icon: settlementType.icon,
              codes: new Set()
            };
          }
          
          groups[groupName].codes.add(settlementClass);
        }
      }
    });

    // Convert Sets to arrays
    Object.keys(groups).forEach(groupName => {
      groups[groupName].codes = Array.from(groups[groupName].codes);
    });

    return groups;
  };

  // Function to derive community types from API data using settlementTypes.json
  const deriveCommunityTypes = (allThePoints) => {
    if (!allThePoints || !Array.isArray(allThePoints)) {
      return {};
    }

    const types = {};
    
    allThePoints.forEach(point => {
      const settlementClass = point.SettlementClassification || "";
      if (settlementClass.trim()) {
        const settlementType = settlementTypes[settlementClass];
        
        if (settlementType) {
          types[settlementClass] = {
            group: settlementType.group,
            type: settlementType.type,
            color: colorMapping[settlementType.color] || "#CCCCCC",
            shape: settlementType.shape,
            icon: settlementType.icon
          };
        }
      }
    });

    return types;
  };

  // Render icon from legend folder
  const renderIcon = (iconName, size = 16) => {
    const iconSrc = iconMapping[iconName];
    if (iconSrc) {
      return (
        <img 
          src={iconSrc} 
          alt={iconName} 
          style={{ width: size, height: size }}
        />
      );
    } else {
      // Fallback to SVG shape if icon not found
      return renderShape("circle", "#CCCCCC", size);
    }
  };

  // Render custom SVG shape as fallback
  const renderShape = (shape, color, size = 16) => {
    const halfSize = size / 2;
    
    switch (shape) {
      case 'circle':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={halfSize} cy={halfSize} r={halfSize - 1} fill={color} stroke="#fff" strokeWidth="1" />
          </svg>
        );
      case 'square':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <rect x="1" y="1" width={size - 2} height={size - 2} fill={color} stroke="#fff" strokeWidth="1" />
          </svg>
        );
      case 'triangle':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <polygon 
              points={`${halfSize},1 ${size - 1},${size - 1} 1,${size - 1}`} 
              fill={color} 
              stroke="#fff" 
              strokeWidth="1" 
            />
          </svg>
        );
      case 'diamond':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <polygon 
              points={`${halfSize},1 ${size - 1},${halfSize} ${halfSize},${size - 1} 1,${halfSize}`} 
              fill={color} 
              stroke="#fff" 
              strokeWidth="1" 
            />
          </svg>
        );
      case 'star':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <polygon 
              points={`${halfSize},1 ${halfSize + 2},${halfSize - 2} ${size - 1},${halfSize} ${halfSize + 2},${halfSize + 2} ${halfSize},${size - 1} ${halfSize - 2},${halfSize + 2} 1,${halfSize} ${halfSize - 2},${halfSize - 2}`} 
              fill={color} 
              stroke="#fff" 
              strokeWidth="1" 
            />
          </svg>
        );
      case 'magen_david':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <polygon 
              points={`${halfSize},1 ${halfSize + 2},${halfSize - 2} ${size - 1},${halfSize} ${halfSize + 2},${halfSize + 2} ${halfSize},${size - 1} ${halfSize - 2},${halfSize + 2} 1,${halfSize} ${halfSize - 2},${halfSize - 2}`} 
              fill={color} 
              stroke="#fff" 
              strokeWidth="1" 
            />
          </svg>
        );
      case 'donut':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={halfSize} cy={halfSize} r={halfSize - 1} fill="none" stroke={color} strokeWidth="3" />
          </svg>
        );
      case 'question':
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <text x={halfSize} y={halfSize + 4} textAnchor="middle" fill={color} fontSize="12" fontWeight="bold">?</text>
          </svg>
        );
      default:
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={halfSize} cy={halfSize} r={halfSize - 1} fill={color} stroke="#fff" strokeWidth="1" />
          </svg>
        );
    }
  };

  // Use provided group data or derive from allThePoints
  const g = groupData || deriveCommunityGroups(allThePoints)[group];
  const communityTypes = deriveCommunityTypes(allThePoints);

  const handleCheckboxChange = (event) => {
    setIsChecked(group, event.target.checked);
  };

  useEffect(() => {
    const el = collapsibleRef.current;
    if (!el) return;

    if (isOpen) {
      el.style.maxHeight = el.scrollHeight + "px";
      const onTransitionEnd = () => {
        el.style.maxHeight = "auto";
        el.removeEventListener("transitionend", onTransitionEnd);
      };
      el.addEventListener("transitionend", onTransitionEnd);
    } else {
      el.style.maxHeight = el.scrollHeight + "px";
      void el.offsetHeight;
      el.style.maxHeight = "0px";
    }
  }, [isOpen]);

  // If no group data is available, don't render
  if (!g) {
    return null;
  }

  return (
    <div className="flex flex-row items-start gap-3">
      <div className="mt-1">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleCheckboxChange}
        ></input>
      </div>
      <div
        className="grow rounded-md"
        style={{
          background: "#232323",
          color: "#fff",
        }}
      >
        <div
          style={{
            backgroundColor: g.color + (isChecked ? "33" : "1A"), // 20% opacity if checked, 10% if not checked
            border: "solid 1px " + g.color,
          }}
          className="p-3 rounded-md"
        >
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center gap-3">
              {g.icon ? renderIcon(g.icon) : renderShape(g.shape, g.color)}
              <h3 className="text-sm font-medium my-1">{g.name}</h3>
            </div>
            <button onClick={() => toggleOpen(group)}>
              {g.codes && advancedMode ? (
                <span className="text-xl">{isOpen ? "▾" : "◂"}</span>
              ) : (
                <span></span>
              )}
            </button>
          </div>
          {g.codes && advancedMode && (
            <>
              <hr
                style={{
                  height: "1px",
                  border: 0,
                  borderTop: "1px solid" + g.color,
                  margin: "5px",
                }}
              />

              <div
                ref={collapsibleRef}
                style={{
                  maxHeight: isOpen ? "auto" : "0px",
                }}
                className="overflow-hidden transition-all duration-300 ease-out"
              >
                {g.codes.map((communityCode, j) => {
                  const c = communityTypes[communityCode];
                  if (!c) return null;
                  return (
                    <div key={j} className="flex items-center gap-3 ml-3">
                      {c.icon ? renderIcon(c.icon) : renderShape(c.shape, c.color)}
                      <p className="text-xs my-2">{c.type}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegendGroup;
